import styles from './Deals.module.css';

import type { ColorVariant } from '../../theme';
import type { ApiDeliveryDeal } from 'src/types/delivery';
import type { ApiDirectPurchaseItem } from 'src/types/directPurchase';

import { isEmpty } from 'lodash';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { ArrowCounterClockwise } from '@phosphor-icons/react';

import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { formatMoney } from 'src/utils/money';
import { resolveDealChain } from 'src/utils/dealChain';
import { formatImageUrl } from 'src/utils/format-image';
import { combinedScreenState } from 'src/utils/requestError';
import { noPhotoAlt, carPhotoAlt } from 'src/utils/imageAlt';
import { isDeliveryQuotePending } from 'src/utils/deliveryInvoice';

import currencyImage from 'src/assets/images/currency.png';
import { deliveryStatusMap, directPurchaseStatusMap } from 'src/constants';
import { useBuyerChains, useDeliveryDeals, useListBuyerDirectPurchases } from 'src/queries';

import { Loader } from 'src/components/Loader';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { ActionButton } from 'src/components/themed/ActionButton';
import { RequestFailureState, WalletNotLinkedState } from 'src/components/EmptyState';

/**
 * The buyer's own purchases: delivery deals (escrow, deal v2/v3) and Buy Now direct transfers.
 * Both are "I bought a car", so they share one list ordered by when they happened — the buyer
 * should not have to know which mechanism paid the dealer to find their own purchase.
 *
 * A failed request is said as such rather than as "no purchases yet" (app#210): the two used to
 * share one branch, so a broken api or a lost connection told the buyer their orders were gone.
 * Both lists make this screen, so a failure of either is said, not shown as an empty list.
 *
 * An account that has not linked a wallet is not a failure: both lists answer `WALLET_NOT_LINKED`,
 * and the screen explains where purchases come from and leads to linking one (app#241).
 */

type PurchaseRow =
  | { kind: 'delivery'; createdAt: string; deal: ApiDeliveryDeal }
  | { kind: 'direct'; createdAt: string; purchase: ApiDirectPurchaseItem };

export const Purchases = () => {
  const deliveryQuery = useDeliveryDeals();
  const { data: deliveryData, refetch: refetchDelivery, isRefetching: deliveryRefetching } = deliveryQuery;

  const directQuery = useListBuyerDirectPurchases();
  const { data: directData, refetch: refetchDirect, isRefetching: directRefetching } = directQuery;

  const state = combinedScreenState(useQueryScreenState(deliveryQuery), useQueryScreenState(directQuery));
  const { data: buyerChains } = useBuyerChains();

  const rows = useMemo<PurchaseRow[]>(() => {
    const deliveries: PurchaseRow[] = (deliveryData?.items ?? []).map((deal) => ({
      kind: 'delivery',
      createdAt: deal.createdAt,
      deal,
    }));
    const directs: PurchaseRow[] = (directData?.items ?? []).map((purchase) => ({
      kind: 'direct',
      createdAt: purchase.createdAt,
      purchase,
    }));

    return [...deliveries, ...directs].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  }, [deliveryData?.items, directData?.items]);

  const isLoading = state === 'loading' || deliveryRefetching || directRefetching;

  const handleRefresh = () => {
    refetchDelivery();
    refetchDirect();
  };

  return (
    <Stack width="100%" gap={4}>
      <Stack width="100%" flexDirection="row" justifyContent="flex-end" alignItems="center">
        <ActionButton onClick={handleRefresh} label="Refresh the list">
          <ArrowCounterClockwise size={24} color="white" />
        </ActionButton>
      </Stack>

      {isLoading ? (
        <Loader />
      ) : state === 'wallet-not-linked' ? (
        <WalletNotLinkedState />
      ) : state !== 'ready' ? (
        <RequestFailureState kind={state} onRetry={handleRefresh} />
      ) : isEmpty(rows) ? (
        <Stack gap={2} alignItems="center" p={4} mt={8}>
          <Typography color="lightWhite" textAlign="center">
            You have no purchases yet
          </Typography>
        </Stack>
      ) : (
        <Stack width="100%" gap={4}>
          {rows.map((row) =>
            row.kind === 'delivery' ? (
              <DeliveryRow
                key={`delivery-${row.deal.id}`}
                deal={row.deal}
                buyerChains={buyerChains}
                to={`/delivery/${row.deal.id}`}
              />
            ) : (
              <DirectRow
                key={`direct-${row.purchase.id}`}
                purchase={row.purchase}
                // A purchase whose listing is gone has nowhere to lead, and now says so by
                // not offering the click at all — it used to look clickable and do nothing.
                to={row.purchase.listing ? `/listing/${row.purchase.listing.id}` : undefined}
              />
            )
          )}
        </Stack>
      )}
    </Stack>
  );
};

interface DeliveryRowProps {
  deal: ApiDeliveryDeal;
  buyerChains: ReturnType<typeof useBuyerChains>['data'];
  to: string;
}

const DeliveryRow = ({ deal, buyerChains, to }: DeliveryRowProps) => {
  const statusInfo = deliveryStatusMap[deal.status];
  const quotePending = isDeliveryQuotePending(deal);
  // Same rule as the deal screen: the ticker is the deal's settlement token, not a constant. A EUR
  // deal on Base reading "18000 USDT" here and "18,348 EURC" one tap later is the kind of
  // contradiction that ends in a support ticket.
  const token = resolveDealChain({
    chainKey: deal.chainKey,
    buyerChains,
    listingCurrency: deal.listing?.currency,
  })?.token;
  const amount = (value: number) => (token ? formatMoney(value, token.symbol) : String(value));

  return (
    <ListItem
      title={
        deal.listing
          ? `${deal.listing.make.name} ${deal.listing.model.name}${
              deal.listing.year ? ` ${deal.listing.year}` : ''
            }`
          : `Order ${deal.orderRef}`
      }
      description={deal.listing?.vin ?? deal.citySnapshot}
      to={to}
      status={
        <div className={styles.status} style={{ backgroundColor: statusInfo?.color }}>
          <Typography color={(statusInfo?.textColor || 'white') as ColorVariant} variant="small">
            {statusInfo?.shortLabel}
          </Typography>
        </div>
      }
      image={
        deal.listing && !isEmpty(deal.listing.images) ? (
          <img
            src={formatImageUrl(deal.listing.images[0])}
            alt={carPhotoAlt(deal.listing)}
            className={styles.image}
          />
        ) : (
          <img
            src={currencyImage}
            alt={noPhotoAlt(deal.listing)}
            className={classNames([styles.image, styles.defaultImage])}
          />
        )
      }
    >
      <Stack width="100%" gap={1}>
        <Stack flexDirection="row" gap={2} alignItems="center" justifyContent="space-between" flex={1}>
          <Typography color="white">Delivery to:</Typography>
          <Typography color="lightWhite">{deal.citySnapshot}</Typography>
        </Stack>
        <Stack flexDirection="row" gap={2} alignItems="center" justifyContent="space-between" flex={1}>
          <Typography color="white">Car price:</Typography>
          <Typography color="lightWhite">{amount(deal.priceAmount)}</Typography>
        </Stack>
        <Stack flexDirection="row" gap={2} alignItems="center" justifyContent="space-between" flex={1}>
          <Typography color="white">Delivery:</Typography>
          <Typography color="lightWhite">
            {quotePending ? 'Set by dealer' : amount(deal.deliveryAmount as number)}
          </Typography>
        </Stack>
      </Stack>
    </ListItem>
  );
};

interface DirectRowProps {
  purchase: ApiDirectPurchaseItem;
  to?: string;
}

const DirectRow = ({ purchase, to }: DirectRowProps) => {
  const statusInfo = directPurchaseStatusMap[purchase.status];

  return (
    <ListItem
      title={
        purchase.listing
          ? `${purchase.listing.make.name} ${purchase.listing.model.name}${
              purchase.listing.year ? ` ${purchase.listing.year}` : ''
            }`
          : 'Direct purchase'
      }
      description={purchase.listing?.vin}
      to={to}
      status={
        <div className={styles.status} style={{ backgroundColor: statusInfo?.color }}>
          <Typography color={(statusInfo?.textColor || 'white') as ColorVariant} variant="small">
            {statusInfo?.shortLabel}
          </Typography>
        </div>
      }
      image={
        purchase.listing && !isEmpty(purchase.listing.images) ? (
          <img
            src={formatImageUrl(purchase.listing.images[0])}
            alt={carPhotoAlt(purchase.listing)}
            className={styles.image}
          />
        ) : (
          <img
            src={currencyImage}
            alt={noPhotoAlt(purchase.listing)}
            className={classNames([styles.image, styles.defaultImage])}
          />
        )
      }
    >
      <Stack width="100%" gap={1}>
        <Stack flexDirection="row" gap={2} alignItems="center" justifyContent="space-between" flex={1}>
          <Typography color="white">Price:</Typography>
          <Typography color="lightWhite">
            {purchase.amount} {purchase.currency}
          </Typography>
        </Stack>
      </Stack>
    </ListItem>
  );
};
